import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';

import { MetadataType } from '../monitoring';

interface DynatraceEvent {
  data: string | Error;
  scope?: string;
  metadata?: MetadataType;
  isError?: boolean;
}

const DYNATRACE_CUSTOM_PROPERTIES_PREFIX = 'event_properties.';
// keys that should not be prefixed
const KNOWN_DYNATRACE_KEYS = ['duration'];

function addEventPropertiesPrefix(values: Record<string, unknown>) {
  const prefixedMetadata: Record<string, unknown> = {};

  Object.entries(values).forEach(([key, value]) => {
    if (
      key.startsWith(DYNATRACE_CUSTOM_PROPERTIES_PREFIX) ||
      KNOWN_DYNATRACE_KEYS.indexOf(key) > -1
    ) {
      prefixedMetadata[key] = value;
    } else {
      prefixedMetadata[`${DYNATRACE_CUSTOM_PROPERTIES_PREFIX}${key}`] = value;
    }
  });

  return prefixedMetadata;
}

function getMessageWithScope(data: unknown, scope?: string): Error | string {
  const message = typeof data === 'string' ? data : (data as Error).message;

  return `[${scope || 'UNSCOPED'}] - ${message}`;
}

//This function is used to spread the scope property value to higher level since dynatrace doesn't support nested object
function spreadScopedPropertiesForRootLevel(metadata: MetadataType) {
  if (!metadata.scope) {
    return metadata;
  }

  const scope = metadata.scope;
  const { metadata: scopePropertiesMetadata, ...scopeProperties } =
    (metadata?.[scope] as Record<string, unknown>) || {};

  // We add custom property with scope as a key in report api - https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/exo-skeleton-api/monitoring.ts#L77
  // We need to spread the scope property value to higher level since dynatrace doesn't support nested object
  const dynatraceMetadata: MetadataType = {
    ...metadata,
    ...(scopePropertiesMetadata && typeof scopePropertiesMetadata === 'object'
      ? scopePropertiesMetadata
      : {}),
    ...scopeProperties,
  };

  // We need to remove scope field from metadata to avoid duplication once they will support objects (currently doesn't impact anything)
  if (scope && metadata.hasOwnProperty(scope)) {
    delete dynatraceMetadata[scope]; //:TODO: Remove scope field after spread value, currently don't work
  }

  return dynatraceMetadata;
}

function convertDatePropertiesToTimestamp(event: MetadataType) {
  return Object.entries(event).reduce(
    (convertedEvent: MetadataType, [key, value]) => {
      convertedEvent[key] = value instanceof Date ? value.getTime() : value;
      return convertedEvent;
    },
    {},
  );
}

export function sendEventToDynatrace({
  data,
  scope,
  isError,
  metadata = {},
}: DynatraceEvent) {
  try {
    const env = getEnv();

    if (env === ENVS.PRODUCTION || !window?.dynatrace) {
      return;
    }

    const wrappedMessage = getMessageWithScope(data, scope);
    const dynatraceMetadata: MetadataType =
      spreadScopedPropertiesForRootLevel(metadata);

    const isErrorObject = data instanceof Error;
    window.dynatrace.sendEvent({
      ...addEventPropertiesPrefix(
        convertDatePropertiesToTimestamp({
          scope,
          ...dynatraceMetadata,
          message: wrappedMessage,
          type: isError ? 'error' : 'info',
          ...(isErrorObject ? { stack: (data as Error).stack } : {}),
        }),
      ),
    });
  } catch (e) {
    console.error(`Failed to send event to Dynatrace: ${e}`);
  }
}
